import { useStatsigClient } from '@statsig/react-bindings';

import { QUESTION_TYPES } from '@api/instant_survey';
import type { Question } from '@api/instant_survey/utilities/types';
import { GATES } from '@constants/statsig';
import { Card, Checkbox, OverlayTriggerTooltip, ThumbsDownIcon, ThumbsUpIcon } from '@utilities';

import AnswersFormat from './components/AnswersFormat';
import EditableText from '../EditableText';
import MaxAnswers from './components/MaxAnswers';
import MaxTotal from './components/MaxTotal';
import NumberOfSets from './components/NumberOfSets';
import OptionsPerSet from './components/OptionsPerSet';
import ValuesBetween from './components/ValuesBetween';

import { QUESTION_MAX_DIFF_LABEL_LENGTH, VALIDATION_MESSAGES } from '../../utilities/constants';

import styles from './_index.module.scss';

export interface QuestionPropertiesProps {
  question: Question;
  updateQuestionProperties: (change: Partial<Question['properties']>) => void;
}

const QuestionProperties = ({ question, updateQuestionProperties }: QuestionPropertiesProps) => {
  const { client } = useStatsigClient();
  const isCalypso = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);

  const { properties, type } = question;

  const isCheckbox = type === QUESTION_TYPES.CHECKBOX.value;
  const isContSum = type === QUESTION_TYPES.CONTSUM.value;
  const isGrid = type === QUESTION_TYPES['TABLE-RADIO'].value;
  const isInstructions = type === QUESTION_TYPES.INSTRUCTIONS.value;
  const isMaxDiff = type === QUESTION_TYPES.MAX_DIFF.value;
  const isNumeric = type === QUESTION_TYPES.NUMERIC.value;
  const isOpenEnd = type === QUESTION_TYPES.ESSAY.value;
  const isRadio = type === QUESTION_TYPES.RADIO.value;
  const isRank = type === QUESTION_TYPES['RANK-DRAGDROP'].value;
  const isTurf = type === QUESTION_TYPES.TURF.value;
  const isVideo = type === QUESTION_TYPES.VIDEO.value;

  const canMaxAnswers = isCheckbox || isTurf || (isRank && isCalypso);
  const canRandomizeOptions = isCheckbox || isContSum || isRadio || isRank || isTurf;

  if (isInstructions || isOpenEnd || isVideo) return null;

  return (
    <Card className={styles['question-properties']}>
      {canRandomizeOptions && (
        <div className={styles['question-properties-option']}>
          <Checkbox
            isChecked={properties.randomizeOptions}
            label="Randomize order"
            onChange={() =>
              updateQuestionProperties({ randomizeOptions: !properties.randomizeOptions })
            }
            value={properties.randomizeOptions}
          />
        </div>
      )}
      {isGrid && (
        <div className={styles['question-properties-option']}>
          <Checkbox
            isChecked={properties.randomizeSubQuestions}
            label="Randomize question rows order"
            onChange={() =>
              updateQuestionProperties({ randomizeSubQuestions: !properties.randomizeSubQuestions })
            }
            value={properties.randomizeSubQuestions}
          />
        </div>
      )}

      {canMaxAnswers && (
        <div className={styles['question-properties-option']}>
          <MaxAnswers
            question={question}
            setMaxAnswers={(value: number) => updateQuestionProperties({ maxAnswers: value })}
          />
        </div>
      )}

      {(isContSum || isNumeric) && (
        <div className={styles['question-properties-option']}>
          <AnswersFormat question={question} updateQuestionProperties={updateQuestionProperties} />
          <ValuesBetween question={question} updateQuestionProperties={updateQuestionProperties} />
          {isContSum && (
            <MaxTotal question={question} updateQuestionProperties={updateQuestionProperties} />
          )}
        </div>
      )}

      {isMaxDiff && (
        <div className={styles['question-properties-option']}>
          <div className={styles['question-properties-label-wrapper']}>
            <OverlayTriggerTooltip content="Best Label">
              <ThumbsUpIcon />
            </OverlayTriggerTooltip>
            {/* @ts-ignore */}
            <EditableText
              aria-label="Best Label"
              error={
                properties.bestLabel && properties.bestLabel.length > QUESTION_MAX_DIFF_LABEL_LENGTH
                  ? VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_LENGTH_ERROR
                  : null
              }
              maxLength={QUESTION_MAX_DIFF_LABEL_LENGTH}
              onChange={(value: string) => updateQuestionProperties({ bestLabel: value })}
              value={properties.bestLabel}
            />
          </div>
          <div className={styles['question-properties-label-wrapper']}>
            <OverlayTriggerTooltip content="Worst Label">
              <ThumbsDownIcon />
            </OverlayTriggerTooltip>
            {/* @ts-ignore */}
            <EditableText
              aria-label="Worst Label"
              error={
                properties.worstLabel &&
                properties.worstLabel.length > QUESTION_MAX_DIFF_LABEL_LENGTH
                  ? VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_LENGTH_ERROR
                  : null
              }
              maxLength={QUESTION_MAX_DIFF_LABEL_LENGTH}
              onChange={(value: string) => updateQuestionProperties({ worstLabel: value })}
              value={properties.worstLabel}
            />
          </div>
          <span className={styles['question-properties-divider']} />
          <OptionsPerSet
            question={question}
            setOptionsPerSet={(value: number) => updateQuestionProperties({ optionsPerSet: value })}
          />
          <span className={styles['question-properties-divider']} />
          <NumberOfSets
            question={question}
            setNumberOfSets={(value: number) => updateQuestionProperties({ numberOfSets: value })}
          />
        </div>
      )}
    </Card>
  );
};

export default QuestionProperties;
